"use client";

/**
 * Experiencia publica do estabelecimento, compartilhada por duas rotas:
 *
 * - `/[slug]`          -> mode="profile": perfil, unidades, conta e o botao
 *                          "Agendar agora". Sem etapas do wizard.
 * - `/[slug]/agendar`  -> mode="booking": so o wizard, com cabecalho fino.
 *
 * Antes tudo isto era uma pagina so, que tentava ser perfil e etapa 1 ao mesmo
 * tempo e por isso disputava espaco vertical no celular. Um componente unico
 * com uma prop de modo evita duplicar a logica (3.000 linhas) entre as rotas.
 *
 * Integração: calcula amountCents a partir da seleção de serviços e passa
 * bookingPayload + amountCents ao BookingPaymentModal.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useFeedbackState } from "../../components/ui/ToastProvider";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiUser,
} from "react-icons/fi";
import {
  getBookingAuthToken,
  hexToRgba,
  resolveImageUrl,
  shouldClearCustomerSession,
  todayYMD,
} from "./utils";
import {
  getBookingContactValidationError,
  isBookingContactComplete,
} from "./utils/bookingValidation";
import {
  clearBookingDraft,
  getCustomerLockedFields,
  loadBookingDraft,
  mergeBookingDraftFormData,
  saveBookingDraft,
} from "./utils/bookingDraft";
import type {
  BookingDecisionContext,
  BookingDecisionSubmitResult,
} from "./hooks/useBookingDecision";
import { BookingDecisionDialog } from "./components/BookingDecisionDialog";
import { useBookingDecision } from "./hooks/useBookingDecision";
import { buildGuestServicePriceLabel } from "./utils/guestPrice";
import {
  buildBookingSummaryPriceLabel,
  getBookingSummaryProviderSubtitle,
} from "./utils/bookingSummary";
import { shouldAutoOpenMembershipAlert } from "./utils/membershipPrompt";
import { shouldLoadPublicDependencies } from "./utils/publicDependencies";
import {
  buildCustomerReviewPromptStorageKey,
  buildCustomerReviewPromptStorageValue,
  parseCustomerReviewPromptStorageValue,
  selectLatestPendingCustomerReview,
  shouldOpenCustomerReviewPrompt,
} from "./utils/customerReviewPrompt";
import type { BookingFormData, EstablishmentPublic } from "./types";
import { LoadingScreen } from "../../components/ui/LoadingScreen";
import {
  createPublicAppointmentHold,
  getPublicProducts,
  getPublicServices,
  getPublicSiblingEstablishments,
  trackPublicFunnelEvent,
  type PublicBranch,
  type PublicProduct,
} from "../../services/public";
import {
  emptyMarketingPixelsConfig,
  getPublicMarketingPixelsConfig,
  type MarketingPixelsConfig,
} from "../../services/marketingPixels";
import {
  clearCustomerToken,
  getCustomerToken,
  saveCustomerToken,
} from "../../lib/customerAuth";
import { getCustomerMe } from "../../services/customerAuth";
import { getPriceQuote } from "../../services/bookingPaymentClient";
import {
  getCustomerReviews,
  submitCustomerReview,
  type CustomerReviewItem,
} from "../../services/customerReviews";
import {
  getPublicCustomerMembershipPlan,
  type CustomerMembershipPlan,
} from "../../services/customerMemberships";
import {
  formatQuantityForUnit,
  maskQuantityInput,
  quantityInputToNumber,
  unitAllowsFraction,
} from "../../lib/inventoryQuantity";
import { formatDurationMinutes } from "../../lib/time";
import { getBranchFallbackCandidates } from "./utils/branchFallback";
import { formatClosureNoticeTitle } from "./utils/unitClosures";
import { startPublicPixelTracking } from "./utils/pixelTracking";

// Hooks
import {
  useAvailability,
  useBannerStyle,
  useBooking,
  useBookingPayment,
  useCustomFields,
  useBookingWizard,
  useProviderSelection,
  usePublicEstablishment,
  useServiceSelection,
} from "./hooks";

// Components
import {
  BookingAccountStep,
  BookingConfirmationScreen,
  BookingForm,
  BookingShowcaseStep,
  BookingWizardHeader,
  BookingWizardNav,
  ConflictSuggestions,
  CustomerReviewPromptModal,
  DatePicker,
  ErrorMessage,
  EstablishmentBanner,
  ImageModal,
  LocationInfo,
  ProviderSelector,
  ProviderTimeSlotsGrid,
  ServiceSelector,
  TimeSlotGrid,
} from "./components";
import { PublicNotFound } from "./components/PublicNotFound";
import { ProfileBookingEntry } from "./components/ProfileBookingEntry";
import { useOfferOpenedTracking } from "./hooks/useOfferLanding";
import { BookingWizardTopBar } from "./components/BookingWizardTopBar";
import { buildSlugPath, type PublicBookingMode } from "./utils/publicRoutes";
import { buildBrandCssVariables } from "../../lib/brandScale";
import { usePwaInstall } from "../../components/app/pwa/PwaInstallProvider";

/**
 * Lê establishment id do sessionStorage com segurança.
 */
function getStoredEstablishmentId(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = sessionStorage.getItem("establishment-cache");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ?? null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Não foi possível ler establishment-cache do sessionStorage", err);
    return null;
  }
}

function getStoredEstablishmentCache(): any | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = sessionStorage.getItem("establishment-cache");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatNameFromSlug(slug?: string | null): string {
  const value = String(slug || "").trim();
  if (!value) return "Estabelecimento";
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function onlyDigits(value: string): string {
  return String(value || "").replace(/\D/g, "");
}

function formatWhatsapp(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  const ddd = digits.slice(0, 2);
  const part1 = digits.length > 10 ? digits.slice(2, 7) : digits.slice(2, 6);
  const part2 = digits.length > 10 ? digits.slice(7, 11) : digits.slice(6, 10);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${ddd}) ${part1}`;
  return `(${ddd}) ${part1}-${part2}`;
}

function formatPublicProductPrice(salePriceCents: number): string {
  return (Number(salePriceCents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPublicBookingDate(value: string): string {
  if (!value) return "Data não selecionada";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return value;
  }
}

function normalizeHexColor(value?: string | null): string | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw;
  if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return null;
}

function getReadableTextColor(backgroundHex: string): string {
  const normalized = normalizeHexColor(backgroundHex);
  if (!normalized) return "#FFFFFF";
  const hex = normalized.slice(1);
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 140 ? "#0F172A" : "#FFFFFF";
}

function isEnabledByDefault(value: unknown): boolean {
  if (value === false || value === 0 || value === "0") return false;
  return true;
}

type CustomerProfile = {
  id: string;
  fullName: string;
  email: string;
  whatsapp: string;
  phone: string;
  photoUrl?: string | null;
};

interface PublicBookingExperienceProps {
  mode: PublicBookingMode;
}

export function PublicBookingExperience({ mode }: PublicBookingExperienceProps) {
  const isProfileMode = mode === "profile";
  const isBookingMode = mode === "booking";
  const router = useRouter();

  const {
    establishment,
    loading,
    error: establishmentError,
    notFound: establishmentNotFound,
    slug,
  } = usePublicEstablishment();
  // OFR-19: o perfil aberto por um link de oferta do WhatsApp grava o evento.
  useOfferOpenedTracking(isProfileMode ? slug : null);
  const customerRedirectToThisSlug = buildSlugPath(slug, "profile");
  const bookingWizardPath = buildSlugPath(slug, "booking");
  const [hasBookingDraft, setHasBookingDraft] = useState(false);
  useEffect(() => {
    if (!isProfileMode || !slug) {
      setHasBookingDraft(false);
      return;
    }
    setHasBookingDraft(Boolean(loadBookingDraft(slug)));
  }, [isProfileMode, slug]);
  const [pixelConfig, setPixelConfig] = useState<MarketingPixelsConfig>(
    emptyMarketingPixelsConfig
  );

  const subscriptionRequiredError =
    Boolean(establishmentError) &&
    /subscription_required|assinatura\s+esta\s+inativa|assinatura\s+está\s+inativa/i.test(
      String(establishmentError)
    );
  const establishmentDeactivatedError =
    Boolean(establishmentError) &&
    /establishment_deactivated|unidade\s+esta\s+desativada|unidade\s+está\s+desativada/i.test(
      String(establishmentError)
    );
  const hasPublicAccessError = subscriptionRequiredError || establishmentDeactivatedError;

  const cachedEstablishment =
    typeof window !== "undefined" ? getStoredEstablishmentCache() : null;
  const sameSlugCache =
    String(cachedEstablishment?.slug || "").trim() === String(slug || "").trim();

  const fallbackInactiveEstablishment: EstablishmentPublic | null =
    hasPublicAccessError && slug
      ? {
          id: String(
            cachedEstablishment?.id || getStoredEstablishmentId() || `inactive-${slug}`
          ),
          name: sameSlugCache
            ? String(cachedEstablishment?.name || "").trim() || formatNameFromSlug(slug)
            : formatNameFromSlug(slug),
          slug: String(slug),
          logoUrl: sameSlugCache ? cachedEstablishment?.logoUrl || null : null,
          backgroundColor: sameSlugCache
            ? cachedEstablishment?.backgroundColor || null
            : null,
          backgroundImageUrl: sameSlugCache
            ? cachedEstablishment?.backgroundImageUrl || null
            : null,
          headerTitleColor: sameSlugCache
            ? cachedEstablishment?.headerTitleColor || null
            : null,
          headerDescriptionColor: sameSlugCache
            ? cachedEstablishment?.headerDescriptionColor || null
            : null,
          infoBoxBackgroundColor: sameSlugCache
            ? cachedEstablishment?.infoBoxBackgroundColor || null
            : null,
          infoBoxTitleColor: sameSlugCache
            ? cachedEstablishment?.infoBoxTitleColor || null
            : null,
          infoBoxTextColor: sameSlugCache
            ? cachedEstablishment?.infoBoxTextColor || null
            : null,
          infoBoxOpacity: sameSlugCache
            ? cachedEstablishment?.infoBoxOpacity || null
            : null,
          infoBoxBlur: sameSlugCache ? cachedEstablishment?.infoBoxBlur || null : null,
          bannerOverlayOpacity: sameSlugCache
            ? cachedEstablishment?.bannerOverlayOpacity || null
            : null,
          aboutText: sameSlugCache ? cachedEstablishment?.aboutText || null : null,
          allowMultiServices: sameSlugCache
            ? Boolean(cachedEstablishment?.allowMultiServices)
            : false,
          socialInstagram: sameSlugCache
            ? cachedEstablishment?.socialInstagram || null
            : null,
          socialTiktok: sameSlugCache ? cachedEstablishment?.socialTiktok || null : null,
          socialYoutube: sameSlugCache
            ? cachedEstablishment?.socialYoutube || null
            : null,
          socialWhatsapp: sameSlugCache
            ? cachedEstablishment?.socialWhatsapp || null
            : null,
          cep: sameSlugCache ? cachedEstablishment?.cep || null : null,
          addressLine: sameSlugCache ? cachedEstablishment?.addressLine || null : null,
          addressNumber: sameSlugCache
            ? cachedEstablishment?.addressNumber || null
            : null,
          addressComplement: sameSlugCache
            ? cachedEstablishment?.addressComplement || null
            : null,
          neighborhood: sameSlugCache ? cachedEstablishment?.neighborhood || null : null,
          city: sameSlugCache ? cachedEstablishment?.city || null : null,
          state: sameSlugCache ? cachedEstablishment?.state || null : null,
          mapLat: sameSlugCache
            ? cachedEstablishment?.mapLat == null
              ? null
              : Number(cachedEstablishment?.mapLat)
            : null,
          mapLng: sameSlugCache
            ? cachedEstablishment?.mapLng == null
              ? null
              : Number(cachedEstablishment?.mapLng)
            : null,
          passCardFeesToClient: sameSlugCache
            ? cachedEstablishment?.passCardFeesToClient || null
            : null,
          timezone: sameSlugCache
            ? String(cachedEstablishment?.timezone || "America/Sao_Paulo")
            : "America/Sao_Paulo",
          slotDurationMinutes: sameSlugCache
            ? Number(cachedEstablishment?.slotDurationMinutes || 30)
            : 30,
          billingActive: establishmentDeactivatedError
            ? 0
            : (cachedEstablishment?.billingActive ?? 1),
          subscriptionStatus: "inactive",
          subscriptionActive: false,
          currentPeriodEnd: null,
          hasActiveAccess: false,
        }
      : null;

  const displayEstablishment: EstablishmentPublic | null =
    establishment || fallbackInactiveEstablishment;

  // GUIA-02: a folha de instalação usa o nome do salão. O provedor não limpa o
  // nome ao trocar de rota, então limpamos ao desmontar.
  const { setBrandName } = usePwaInstall();
  const displayEstablishmentName = displayEstablishment?.name || null;
  useEffect(() => {
    setBrandName(displayEstablishmentName);
    return () => setBrandName(null);
  }, [displayEstablishmentName, setBrandName]);

  /**
   * Cor da marca: a mesma cor de fundo escolhida na Personalizacao passa a
   * pintar botoes, icones e destaques desta pagina. Sem cor escolhida, a
   * escala cai no violeta padrao e nada muda.
   */
  const brandStyle = useMemo(
    () => buildBrandCssVariables(displayEstablishment?.backgroundColor) as CSSProperties,
    [displayEstablishment?.backgroundColor]
  );

  // 🔥 NOVA LÓGICA: Verifica se tem acesso baseado no status e data
  const hasActiveAccess = (() => {
    // Se o back-end já calculou, usa ele (mais seguro)
    if (displayEstablishment?.hasActiveAccess === true) return true;

    // Se não, calcula baseado nos dados
    if (!displayEstablishment) return false;

    const now = new Date();
    const periodEnd = displayEstablishment.currentPeriodEnd
      ? new Date(displayEstablishment.currentPeriodEnd)
      : null;

    // Regras de acesso:
    // 1. Status 'active' (mesmo com data passada - em carência) → acesso liberado
    // 2. Status 'past_due' (pagamento atrasado) → acesso liberado (carência)
    // 3. Status 'trial' → acesso liberado
    // 4. Status 'canceled' com data futura → acesso liberado até a data

    switch (displayEstablishment.subscriptionStatus) {
      case "active":
      case "trial":
        return true; // Sempre tem acesso (mesmo em carência)

      case "past_due":
        return true; // Em carência, acesso liberado

      case "canceled":
        // Cancelado: acesso só até a data de fim
        return periodEnd ? periodEnd > now : false;

      case "inactive":
      default:
        return false;
    }
  })();

  const establishmentBillingInactive =
    displayEstablishment != null && Number(displayEstablishment.billingActive ?? 1) !== 1;
  const subscriptionInactive = !hasActiveAccess;
  const blockedByDeactivatedUnit =
    establishmentDeactivatedError || establishmentBillingInactive;
  const publicDependenciesSlug = shouldLoadPublicDependencies({
    slug,
    loading,
    hasEstablishment: Boolean(displayEstablishment),
    hasPublicAccessError,
  })
    ? slug
    : null;

  const [date, setDate] = useState(todayYMD());
  const [selectedTime, setSelectedTime] = useState("");
  const [autoJumped, setAutoJumped] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [quotedServicePrices, setQuotedServicePrices] = useState<
    Record<string, number | null>
  >({});
  const allowAnyProvider = isEnabledByDefault(displayEstablishment?.allowAnyProvider);

  const serviceSelection = useServiceSelection({
    slug: publicDependenciesSlug,
    allowMultiServices: displayEstablishment?.allowMultiServices,
    date,
    selectedProviderId,
  });

  const providerSelection = useProviderSelection({
    slug: publicDependenciesSlug,
    selectedServiceId: serviceSelection.selectedServiceId,
    date,
    selectedProviderId,
    setSelectedProviderId,
  });

  const availability = useAvailability({
    slug: publicDependenciesSlug,
    date,
    selectedServiceId: serviceSelection.selectedServiceId,
    selectedServiceIds: serviceSelection.selectedServiceIds,
    selectedProviderId: providerSelection.selectedProviderId,
    multiEnabled: serviceSelection.multiEnabled,
  });

  const customFields = useCustomFields({
    slug: publicDependenciesSlug,
    selectedServiceId: serviceSelection.selectedServiceId,
  });

  const [formData, setFormData] = useState<BookingFormData>({
    name: "",
    email: "",
    whatsapp: "",
    phone: "",
    notes: "",
    customValues: {},
  });
  const [customerToken, setCustomerToken] = useState<string | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(true);
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);
  // MBR-14: o que a conta já tem fica travado; o que falta (WhatsApp de quem
  // foi cadastrado no balcão, por exemplo) segue editável no formulário.
  const customerLockedFields = useMemo(
    () =>
      isCustomerLoggedIn && customerProfile
        ? getCustomerLockedFields(customerProfile)
        : [],
    [isCustomerLoggedIn, customerProfile]
  );
  const [customerReviewModal, setCustomerReviewModal] =
    useState<CustomerReviewItem | null>(null);
  const [customerPendingReviewIds, setCustomerPendingReviewIds] = useState<string[]>([]);
  const [customerReviewSubmitting, setCustomerReviewSubmitting] = useState(false);
  const [customerReviewError, setCustomerReviewError] = useFeedbackState<string | null>(
    null
  );
  const [showMembershipAlertModal, setShowMembershipAlertModal] = useState(false);
  const [publicMembershipPlans, setPublicMembershipPlans] = useState<
    CustomerMembershipPlan[]
  >([]);
  const [publicProducts, setPublicProducts] = useState<PublicProduct[]>([]);
  const [publicOrderLoading, setPublicOrderLoading] = useState(false);
  const [publicOrderMessage, setPublicOrderMessage] = useFeedbackState<string | null>(
    null,
    "success"
  );
  const [showPublicProductsModal, setShowPublicProductsModal] = useState(false);
  const [publicProductCarouselIndex, setPublicProductCarouselIndex] = useState(0);
  const [publicOrderForm, setPublicOrderForm] = useState({
    customerName: "",
    customerWhatsapp: "",
    customerEmail: "",
    selectedProductId: "",
    quantity: "1",
    notes: "",
  });
  const [bookingProducts, setBookingProducts] = useState<
    Array<{ productId: string; quantity: number }>
  >([]);
  const [publicMembershipEnabled, setPublicMembershipEnabled] = useState(false);
  const [activeMembershipPlanIndex, setActiveMembershipPlanIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [branches, setBranches] = useState<PublicBranch[]>([]);
  const branchFallbackCheckedRef = useRef<string | null>(null);
  /** Cliente respondeu "não" na etapa de conta: não perguntar de novo no submit. */
  const [guestChoiceConfirmed, setGuestChoiceConfirmed] = useState(false);
  const conflictHandledRef = useRef(false);
  const draftRestoredRef = useRef<string | null>(null);

  const bannerStyle = useBannerStyle(displayEstablishment);

  useEffect(() => {
    let cancelled = false;
    if (!publicDependenciesSlug) {
      setBranches([]);
      return;
    }

    getPublicSiblingEstablishments(publicDependenciesSlug)
      .then((list) => {
        if (cancelled) return;
        setBranches(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        setBranches([]);
      });

    return () => {
      cancelled = true;
    };
  }, [publicDependenciesSlug]);

  useEffect(() => {
    if (!publicDependenciesSlug || serviceSelection.loading) return;
    if (serviceSelection.services.length > 0) {
      branchFallbackCheckedRef.current = null;
      return;
    }
    if (branchFallbackCheckedRef.current === publicDependenciesSlug) return;

    const candidates = getBranchFallbackCandidates(publicDependenciesSlug, branches);
    if (candidates.length === 0) return;

    let cancelled = false;
    branchFallbackCheckedRef.current = publicDependenciesSlug;

    (async () => {
      for (const branch of candidates) {
        try {
          const services = await getPublicServices(branch.slug, { date });
          if (!cancelled && Array.isArray(services) && services.length > 0) {
            router.replace(buildSlugPath(branch.slug, mode));
            return;
          }
        } catch {
          // Se uma filial falhar, tenta a próxima disponível.
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    branches,
    mode,
    date,
    publicDependenciesSlug,
    router,
    serviceSelection.loading,
    serviceSelection.services.length,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (!publicDependenciesSlug) {
      setPublicProducts([]);
      setPublicProductCarouselIndex(0);
      return;
    }

    getPublicProducts(publicDependenciesSlug)
      .then((list) => {
        if (cancelled) return;
        const normalized = Array.isArray(list) ? list : [];
        const firstAvailable = normalized.find((item) => item.available);
        setPublicProducts(normalized);
        setPublicProductCarouselIndex(0);
        setPublicOrderForm((previous) => ({
          ...previous,
          selectedProductId:
            previous.selectedProductId || firstAvailable?.id || normalized[0]?.id || "",
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setPublicProducts([]);
        setPublicProductCarouselIndex(0);
      });

    return () => {
      cancelled = true;
    };
  }, [publicDependenciesSlug]);

  useEffect(() => {
    if (publicProducts.length <= 1) return;

    const interval = window.setInterval(() => {
      setPublicProductCarouselIndex((current) => (current + 1) % publicProducts.length);
    }, 4500);

    return () => {
      window.clearInterval(interval);
    };
  }, [publicProducts.length]);

  const selectedPublicOrderProduct = useMemo(
    () =>
      publicProducts.find(
        (product) => product.id === publicOrderForm.selectedProductId
      ) || null,
    [publicOrderForm.selectedProductId, publicProducts]
  );
  const selectedPublicProductAllowsFraction = useMemo(
    () => unitAllowsFraction(selectedPublicOrderProduct?.unitLabel || "un"),
    [selectedPublicOrderProduct?.unitLabel]
  );
  const visiblePublicProductCards = useMemo(() => {
    if (publicProducts.length <= 3) return publicProducts;
    return [0, 1, 2].map(
      (offset) =>
        publicProducts[(publicProductCarouselIndex + offset) % publicProducts.length]
    );
  }, [publicProductCarouselIndex, publicProducts]);
  const publicOrderQuantity = quantityInputToNumber(publicOrderForm.quantity);
  const publicOrderCanSubmit =
    Boolean(selectedPublicOrderProduct?.available) &&
    publicOrderQuantity > 0 &&
    publicOrderQuantity <= Number(selectedPublicOrderProduct?.currentStock || 0);

  /**
   * Carrega a conta do token. Sessão válida é cliente logado, mesmo com perfil
   * incompleto (MBR-14): antes, sem WhatsApp na conta, a pessoa era tratada
   * como visitante e o agendamento saía sem o token, sem a assinatura.
   */
  const loadCustomerSession = useCallback(
    async (
      token: string,
      isCancelled: () => boolean = () => false,
      onSignedIn?: () => void
    ) => {
      setIsLoadingCustomer(true);
      try {
        const me = await getCustomerMe(token);
        if (isCancelled() || !me) return false;
        const fullName = String(me.fullName || "").trim();
        const email = String(me.email || "")
          .trim()
          .toLowerCase();
        const whatsapp = formatWhatsapp(me.whatsapp || me.phone || "");
        const phone = formatWhatsapp(me.phone || me.whatsapp || "");
        const hasWhatsapp = onlyDigits(whatsapp).length >= 10;

        setCustomerProfile({
          id: String(me.id || ""),
          fullName,
          email,
          whatsapp,
          phone,
          photoUrl: me.photoUrl,
        });
        // Só o que a conta tem substitui o formulário; o resto (o WhatsApp
        // que a pessoa digitou antes de entrar) fica como está.
        setFormData((current) => ({
          ...current,
          name: fullName || current.name,
          email: email || current.email,
          whatsapp: hasWhatsapp ? whatsapp : current.whatsapp,
          phone: current.phone || (hasWhatsapp ? whatsapp : ""),
        }));
        setIsCustomerLoggedIn(true);
        // No mesmo lote do estado logado: a etapa "Conta" some da lista e, sem
        // isto, o wizard voltaria por um instante para a primeira etapa.
        onSignedIn?.();
        return true;
      } catch (error) {
        const isUnauthorized = shouldClearCustomerSession(error);
        const isCurrentToken = getCustomerToken() === token;

        if (isUnauthorized && isCurrentToken) {
          void clearCustomerToken({ expectedToken: token, revokePushBindings: false });
        }

        if (!isCancelled() && (!isUnauthorized || isCurrentToken)) {
          if (isUnauthorized) {
            setCustomerToken(null);
          }
          setCustomerProfile(null);
          setIsCustomerLoggedIn(false);
        }
        return false;
      } finally {
        if (!isCancelled()) setIsLoadingCustomer(false);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const token = typeof window !== "undefined" ? getCustomerToken() : null;
    setCustomerToken(token);

    if (!token) {
      setCustomerProfile(null);
      setIsCustomerLoggedIn(false);
      setIsLoadingCustomer(false);
      return;
    }

    void loadCustomerSession(token, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [loadCustomerSession]);

  const customerReviewStorageKey = useMemo(
    () => buildCustomerReviewPromptStorageKey(slug, customerProfile?.id),
    [slug, customerProfile?.id]
  );

  useEffect(() => {
    let cancelled = false;

    if (!customerToken || !isCustomerLoggedIn || !slug) {
      setCustomerReviewModal(null);
      setCustomerPendingReviewIds([]);
      setCustomerReviewError(null);
      return;
    }

    getCustomerReviews(customerToken, { status: "pending" })
      .then((response) => {
        if (cancelled) return;

        const pendingReviews = Array.isArray(response?.pending) ? response.pending : [];
        const pendingAppointmentIds = pendingReviews.map((review) =>
          String(review.appointmentId || "")
        );
        setCustomerPendingReviewIds(pendingAppointmentIds);
        const latestPendingReview = selectLatestPendingCustomerReview(pendingReviews);
        const promptStorageState =
          typeof window !== "undefined"
            ? parseCustomerReviewPromptStorageValue(
                window.localStorage.getItem(customerReviewStorageKey)
              )
            : null;

        if (
          !shouldOpenCustomerReviewPrompt({
            lastSubmittedDateKey: promptStorageState?.lastSubmittedDateKey || null,
            lastSubmittedAt: promptStorageState?.lastSubmittedAt || null,
            latestPendingReviewRequestedAt:
              latestPendingReview?.reviewRequestedAt || null,
            pendingCount: pendingReviews.length,
            seenAppointmentIds: promptStorageState?.seenAppointmentIds ?? null,
            pendingAppointmentIds,
          })
        ) {
          setCustomerReviewModal(null);
          return;
        }

        setCustomerReviewModal(latestPendingReview);
      })
      .catch(() => {
        if (!cancelled) {
          setCustomerReviewModal(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [customerToken, isCustomerLoggedIn, slug, customerReviewStorageKey]);

  useEffect(() => {
    let cancelled = false;
    if (!publicDependenciesSlug) {
      setPublicMembershipEnabled(false);
      setPublicMembershipPlans([]);
      setActiveMembershipPlanIndex(0);
      return;
    }

    getPublicCustomerMembershipPlan(publicDependenciesSlug)
      .then((response) => {
        if (cancelled) return;

        const plans =
          Array.isArray(response?.plans) && response.plans.length > 0
            ? response.plans
            : response?.plan
              ? [response.plan]
              : [];

        const hasPlans = Boolean(response?.enabled) && plans.length > 0;
        setPublicMembershipEnabled(hasPlans);
        setPublicMembershipPlans(hasPlans ? plans : []);
        setActiveMembershipPlanIndex(0);
      })
      .catch(() => {
        if (cancelled) return;
        setPublicMembershipEnabled(false);
        setPublicMembershipPlans([]);
        setActiveMembershipPlanIndex(0);
      });

    return () => {
      cancelled = true;
    };
  }, [publicDependenciesSlug]);

  const hasPublicMembershipPlans =
    publicMembershipEnabled && publicMembershipPlans.length > 0;

  useEffect(() => {
    if (
      !slug ||
      loading ||
      isLoadingCustomer ||
      isCustomerLoggedIn ||
      !hasPublicMembershipPlans
    ) {
      setShowMembershipAlertModal(false);
      return;
    }
    setShowMembershipAlertModal(
      shouldAutoOpenMembershipAlert({
        slug,
        loading,
        isLoadingCustomer,
        isCustomerLoggedIn,
        hasPublicMembershipPlans,
      })
    );
  }, [slug, loading, isLoadingCustomer, isCustomerLoggedIn, hasPublicMembershipPlans]);

  const booking = useBooking({
    slug,
    authToken: getBookingAuthToken(customerToken, isCustomerLoggedIn),
    services: serviceSelection.services,
    multiEnabled: serviceSelection.multiEnabled,
    selectedServiceIds: serviceSelection.selectedServiceIds,
    selectedServiceId: serviceSelection.selectedServiceId,
    selectedProviderId: providerSelection.selectedProviderId,
    requireProviderSelection: !allowAnyProvider,
    date,
    selectedTime,
    customValues: customFields.customValues,
    products: bookingProducts,
    validateCustomFields: customFields.validateCustomFields,
    // CNF-04: a frase de sucesso só promete o WhatsApp quando a unidade manda a
    // confirmação por lá. A rota omite o campo quando bloqueia: ausente é falso.
    whatsappBookingConfirmation: establishment?.whatsappBookingConfirmation === true,
    onSuccess: () => {
      setBookingProducts([]);
      setFormData((current) =>
        isCustomerLoggedIn
          ? {
              ...current,
              notes: "",
              customValues: {},
            }
          : {
              name: "",
              email: "",
              whatsapp: "",
              phone: "",
              notes: "",
              customValues: {},
            }
      );
      setSelectedTime("");
      customFields.clearCustomValues();
    },
  });

  const { loadPaymentStatus, onlinePaymentsEnabled } = useBookingPayment();

  const PAYMENT_SESSION_KEY = "pending-payment";

  const storedEstablishmentId =
    typeof window !== "undefined" ? getStoredEstablishmentId() : null;
  // Prioriza o id carregado pelo slug (fonte da verdade) para evitar cache desatualizado
  const establishmentId = displayEstablishment?.id || storedEstablishmentId || null;
  const trackedPageViewRef = useRef("");
  const trackedFormStartedRef = useRef("");

  useEffect(() => {
    if (!establishmentId || hasPublicAccessError) return;
    void loadPaymentStatus(establishmentId).catch(() => undefined);
  }, [establishmentId, hasPublicAccessError, loadPaymentStatus]);

  useEffect(() => {
    let cancelled = false;
    if (!slug || hasPublicAccessError) {
      setPixelConfig(emptyMarketingPixelsConfig);
      return;
    }

    getPublicMarketingPixelsConfig(slug)
      .then((config) => {
        if (!cancelled) setPixelConfig(config);
      })
      .catch(() => {
        if (!cancelled) setPixelConfig(emptyMarketingPixelsConfig);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, hasPublicAccessError]);

  useEffect(() => {
    return startPublicPixelTracking(pixelConfig);
  }, [pixelConfig]);

  useEffect(() => {
    if (!slug || !establishmentId) return;
    const key = `${slug}:${establishmentId}`;
    if (trackedPageViewRef.current === key) return;
    trackedPageViewRef.current = key;
    trackPublicFunnelEvent(slug, {
      eventName: "page_view",
      metadata: { establishmentId },
    });
  }, [slug, establishmentId]);

  useEffect(() => {
    if (!slug) return;
    const selectedIds = serviceSelection.multiEnabled
      ? serviceSelection.selectedServiceIds
      : serviceSelection.selectedServiceId
        ? [serviceSelection.selectedServiceId]
        : [];
    if (selectedIds.length === 0) return;
    trackPublicFunnelEvent(slug, {
      eventName: "service_selected",
      serviceId: selectedIds[0],
      providerId: providerSelection.selectedProviderId || undefined,
      scheduledDate: date || undefined,
      metadata: {
        selectedServiceIds: selectedIds,
        multi: serviceSelection.multiEnabled,
      },
    });
  }, [
    slug,
    serviceSelection.multiEnabled,
    serviceSelection.selectedServiceId,
    serviceSelection.selectedServiceIds,
    providerSelection.selectedProviderId,
    date,
  ]);

  useEffect(() => {
    if (!slug || !selectedTime) return;
    trackPublicFunnelEvent(slug, {
      eventName: "slot_selected",
      serviceId: serviceSelection.multiEnabled
        ? serviceSelection.selectedServiceIds[0]
        : serviceSelection.selectedServiceId || undefined,
      providerId: providerSelection.selectedProviderId || undefined,
      scheduledDate: date,
      scheduledTime: selectedTime,
    });
  }, [
    slug,
    date,
    selectedTime,
    serviceSelection.multiEnabled,
    serviceSelection.selectedServiceId,
    serviceSelection.selectedServiceIds,
    providerSelection.selectedProviderId,
  ]);

  useEffect(() => {
    if (!slug) return;
    const hasStarted =
      Boolean(formData.name.trim()) ||
      Boolean(formData.email.trim()) ||
      Boolean(formData.whatsapp.trim()) ||
      Boolean(formData.phone.trim()) ||
      Boolean(formData.notes.trim());
    if (!hasStarted) return;
    const key = `${slug}:${date}:${selectedTime || "sem-horario"}`;
    if (trackedFormStartedRef.current === key) return;
    trackedFormStartedRef.current = key;
    trackPublicFunnelEvent(slug, {
      eventName: "form_started",
      serviceId: serviceSelection.multiEnabled
        ? serviceSelection.selectedServiceIds[0]
        : serviceSelection.selectedServiceId || undefined,
      providerId: providerSelection.selectedProviderId || undefined,
      scheduledDate: date,
      scheduledTime: selectedTime || undefined,
    });
  }, [
    slug,
    formData.name,
    formData.email,
    formData.whatsapp,
    formData.phone,
    formData.notes,
    date,
    selectedTime,
    serviceSelection.multiEnabled,
    serviceSelection.selectedServiceId,
    serviceSelection.selectedServiceIds,
    providerSelection.selectedProviderId,
  ]);

  // Data sem horário na primeira carga: pula direto para a próxima data que já
  // tem vaga (achada por `useAvailability`), em vez de deixar o cliente parado
  // num dia sem nada. Só acontece uma vez — se o cliente escolher outra data
  // manualmente depois (handleDateChange reseta `autoJumped`), o card de
  // sugestão no TimeSlotGrid fica disponível, mas não pula sozinho de novo.
  useEffect(() => {
    if (!slug || autoJumped || !availability.suggestedDate) return;
    if (availability.suggestedDate === date) return;
    setDate(availability.suggestedDate);
    setAutoJumped(true);
  }, [slug, availability.suggestedDate, date, autoJumped]);

  useEffect(() => {
    if (allowAnyProvider) return;
    if (!providerSelection.selectedProviderId) {
      setSelectedTime("");
    }
  }, [allowAnyProvider, providerSelection.selectedProviderId]);

  const handleBranchChange = (nextSlug: string) => {
    const normalized = String(nextSlug || "").trim();
    if (!normalized || normalized === slug) return;
    router.push(buildSlugPath(normalized, mode));
  };

  const handleSubmitCustomerReview = async (rating: number, comment: string) => {
    if (!customerToken || !customerReviewModal) return;
    if (rating < 1 || rating > 5) {
      setCustomerReviewError("Escolha uma nota de 1 a 5 estrelas.");
      return;
    }

    setCustomerReviewSubmitting(true);
    setCustomerReviewError(null);

    try {
      await submitCustomerReview(customerToken, customerReviewModal.appointmentId, {
        rating,
        comment: comment.trim(),
      });

      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          customerReviewStorageKey,
          buildCustomerReviewPromptStorageValue(new Date(), [
            ...customerPendingReviewIds,
            customerReviewModal.appointmentId,
          ])
        );
      }

      setCustomerReviewModal(null);
    } catch (error: any) {
      setCustomerReviewError(error?.message || "Não foi possível enviar a avaliação.");
    } finally {
      setCustomerReviewSubmitting(false);
    }
  };

  const handleDismissCustomerReview = () => {
    // Fechar sem avaliar silencia o convite como um envio: ele só volta no
    // dia seguinte ou quando chegar um novo pedido de avaliação.
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          customerReviewStorageKey,
          buildCustomerReviewPromptStorageValue(new Date(), [
            ...customerPendingReviewIds,
            ...(customerReviewModal ? [customerReviewModal.appointmentId] : []),
          ])
        );
      } catch {
        // Sem storage o modal só fecha nesta visita.
      }
    }
    setCustomerReviewModal(null);
    setCustomerReviewError(null);
  };

  const handleCustomerLogout = async () => {
    await clearCustomerToken();
    setCustomerToken(null);
    setCustomerProfile(null);
    setIsCustomerLoggedIn(false);
    setCustomerReviewModal(null);
    setCustomerReviewError(null);
    setShowMembershipAlertModal(false);
    setGuestChoiceConfirmed(false);
    router.replace(buildSlugPath(slug, mode));
  };

  const hasSelectedServiceForWizard = serviceSelection.multiEnabled
    ? serviceSelection.selectedServiceIds.length > 0
    : Boolean(serviceSelection.selectedServiceId);
  const hasContactData = isBookingContactComplete(formData);
  const hasShowcaseProducts = publicProducts.length > 0;

  const wizard = useBookingWizard({
    hasSelectedService: hasSelectedServiceForWizard,
    allowAnyProvider,
    hasProviderOptions: providerSelection.providers.length > 0,
    hasSelectedProvider: Boolean(providerSelection.selectedProviderId),
    hasSelectedDate: Boolean(date),
    hasSelectedTime: Boolean(selectedTime),
    hasContactData,
    hasShowcaseProducts,
    isCustomerLoggedIn,
  });
  const { goToStep: goToWizardStep } = wizard;

  // Conflito 409: as sugestões de horário vivem na etapa de dia e horário,
  // então devolvemos o cliente para lá em vez de deixá-lo no formulário sem
  // saber o que aconteceu.
  useEffect(() => {
    if (!booking.showSuggestions) {
      conflictHandledRef.current = false;
      return;
    }
    if (conflictHandledRef.current) return;
    conflictHandledRef.current = true;
    goToWizardStep("schedule");
  }, [booking.showSuggestions, goToWizardStep]);

  // Volta do cadastro/login: recompõe a seleção salva antes de sair da página.
  useEffect(() => {
    // Restaurar e consumir o rascunho e papel do wizard. No perfil ele so
    // muda o rotulo do botao; consumi-lo ali perderia a selecao da pessoa.
    if (!isBookingMode) return;
    if (!slug || draftRestoredRef.current === slug) return;
    if (serviceSelection.services.length === 0) return;

    draftRestoredRef.current = slug;
    const draft = loadBookingDraft(slug);
    if (!draft) return;

    const availableIds = new Set(serviceSelection.services.map((service) => service.id));
    if (draft.selectedServiceId && availableIds.has(draft.selectedServiceId)) {
      serviceSelection.setSelectedServiceId(draft.selectedServiceId);
    }
    const restoredMultiIds = draft.selectedServiceIds.filter((id) =>
      availableIds.has(id)
    );
    if (restoredMultiIds.length > 0) {
      serviceSelection.setSelectedServiceIds(restoredMultiIds);
    }
    if (draft.selectedProviderId) setSelectedProviderId(draft.selectedProviderId);
    if (draft.date) setDate(draft.date);
    if (draft.selectedTime) setSelectedTime(draft.selectedTime);
    if (draft.formData) {
      // O perfil do cliente pode ter chegado antes do rascunho: os campos
      // vazios salvos na etapa de conta não podem apagar os dados da conta.
      setFormData((previous) =>
        mergeBookingDraftFormData(previous, draft.formData, {
          preserveFields: customerLockedFields,
        })
      );
    }
    if (draft.customValues && Object.keys(draft.customValues).length > 0) {
      customFields.setCustomValues((previous: Record<string, any>) => ({
        ...previous,
        ...draft.customValues,
      }));
    }

    clearBookingDraft(slug);
    goToWizardStep(draft.stepId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, serviceSelection.services.length, goToWizardStep]);

  const decision = useBookingDecision({
    slug,
    contactKey: JSON.stringify([formData.email, formData.whatsapp, formData.phone]),
    selectionKey: JSON.stringify([
      slug,
      date,
      selectedTime,
      providerSelection.selectedProviderId,
      serviceSelection.selectedServiceId,
      serviceSelection.selectedServiceIds,
      bookingProducts,
      customFields.customValues,
      formData.notes,
    ]),
    token: customerToken,
    sessionLoading: isLoadingCustomer,
    services: serviceSelection.services,
    onCustomer: (me, token) => {
      void saveCustomerToken(token);
      setCustomerToken(token);
      const profile = {
        id: String(me.id),
        fullName: me.fullName || "",
        email: me.email || "",
        whatsapp: formatWhatsapp(me.whatsapp || me.phone || ""),
        phone: formatWhatsapp(me.phone || me.whatsapp || ""),
        photoUrl: me.photoUrl,
      };
      setCustomerProfile(profile);
      setIsCustomerLoggedIn(true);
      setFormData((current) => ({
        ...current,
        name: profile.fullName || current.name,
        email: profile.email || current.email,
        whatsapp: profile.whatsapp || current.whatsapp,
        phone: profile.phone || current.phone,
      }));
      goToWizardStep("details");
    },
    onExpired: (token) => {
      void clearCustomerToken({ expectedToken: token, revokePushBindings: false });
      setCustomerToken(null);
      setCustomerProfile(null);
      setIsCustomerLoggedIn(false);
    },
    onUnavailable: () => {
      setSelectedTime("");
      availability.refetch();
      goToWizardStep("schedule");
      booking.setError(
        "Esse horário não está mais disponível. Escolha outro para continuar."
      );
    },
    submit: (payload, token, isCurrent, context) =>
      submitBookingFlow(payload, token, isCurrent, context),
  });

  if (
    !loading &&
    displayEstablishment &&
    (subscriptionInactive || hasPublicAccessError || blockedByDeactivatedUnit)
  ) {
    return (
      <div className="min-h-screen bg-slate-50">
        <EstablishmentBanner
          establishment={displayEstablishment}
          bannerStyle={bannerStyle}
          onImageClick={() => setShowImageModal(true)}
          branches={branches}
          onBranchChange={handleBranchChange}
        />

        <div className="max-w-5xl mx-auto px-4 pb-16">
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
            <h2 className="text-xl font-semibold mb-2">
              {blockedByDeactivatedUnit ? "Unidade desativada" : "Agenda indisponível"}
            </h2>
            <p className="text-sm">
              {blockedByDeactivatedUnit
                ? `A unidade ${displayEstablishment.name} está desativada no momento. Selecione outra unidade ativa para continuar.`
                : "Este estabelecimento está com a assinatura inativa no momento. Tente novamente mais tarde."}
            </p>
          </div>

          <div className="mt-0">
            <LocationInfo establishment={displayEstablishment} showMap={false} />
          </div>

          {slug && (
            <section className="mt-4 mb-4 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Acesso a assinatura
                  </p>
                  <p className="mt-1 text-sm text-amber-900">
                    {isCustomerLoggedIn
                      ? "Conta de cliente reconhecida. Gerencie sua assinatura e seus agendamentos na área do cliente."
                      : hasPublicMembershipPlans
                        ? `${displayEstablishment.name} tem planos de assinatura para clientes. Faça login para contratar.`
                        : "Faça login para preencher dados automaticamente e gerenciar seus agendamentos."}
                  </p>
                  {isLoadingCustomer && (
                    <p className="mt-1 text-xs text-amber-700">
                      Validando sessão do cliente...
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {!isCustomerLoggedIn && (
                    <Link
                      href={`/cliente/login?redirect=${encodeURIComponent(slug ? `/${slug}` : "/")}`}
                      className="inline-flex min-h-[2.75rem] items-center justify-center rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold leading-none text-white transition hover:bg-amber-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
                    >
                      Entrar
                    </Link>
                  )}
                  {isCustomerLoggedIn && (
                    <>
                      <Link
                        href={`/cliente/area?slug=${encodeURIComponent(String(slug || ""))}`}
                        className="inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border border-amber-300 bg-white px-5 py-2.5 text-sm font-semibold leading-none text-amber-900 transition hover:bg-amber-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
                      >
                        Área do cliente
                      </Link>
                      <button
                        type="button"
                        onClick={handleCustomerLogout}
                        className="inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-5 py-2.5 text-sm font-semibold leading-none text-amber-900 transition hover:bg-amber-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100"
                      >
                        Sair da conta
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        {displayEstablishment?.backgroundImageUrl && (
          <ImageModal
            show={showImageModal}
            imageUrl={displayEstablishment.backgroundImageUrl}
            alt={displayEstablishment.name || "Imagem do estabelecimento"}
            onClose={() => setShowImageModal(false)}
          />
        )}
      </div>
    );
  }

  const handleDateChange = (newDate: string) => {
    // Não reseta `autoJumped`: se o cliente escolher manualmente uma data sem
    // horário, ele quis olhar aquela data — não é para o pulo automático
    // brigar com a escolha dele. A sugestão de próxima data com vaga continua
    // disponível como botão no TimeSlotGrid.
    setDate(newDate);
  };

  const handleNextDate = () => {
    const currentIndex = availability.availableDates.indexOf(date);
    const nextIndex =
      currentIndex < availability.availableDates.length - 1 ? currentIndex + 1 : 0;
    setDate(availability.availableDates[nextIndex]);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedTime("");
    // O serviço escolhido pode não existir para o novo profissional (nem todo
    // profissional faz todo serviço) — a etapa de serviço refiltra a partir daqui.
    serviceSelection.setSelectedServiceId("");
    serviceSelection.setSelectedServiceIds([]);
    providerSelection.setSelectedProviderId(providerId);
    // Novo profissional muda toda a disponibilidade: vale tentar o pulo
    // automático de novo, mesmo que já tivesse acontecido antes.
    setAutoJumped(false);
  };

  const handleProviderSlotClick = (providerId: string, time: string) => {
    providerSelection.setSelectedProviderId(providerId);
    setSelectedTime(time);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSuggestionSelect = (time: string, providerId?: string) => {
    if (providerId) providerSelection.setSelectedProviderId(providerId);
    setSelectedTime(time);
    booking.setShowSuggestions(false);
  };

  const getServiceDisplayedPrice = (service: {
    id: string;
    price?: number | null;
    showPrice?: boolean;
  }) => {
    if (!service.showPrice || typeof service.price !== "number") return null;
    if (onlinePaymentsEnabled === false) return service.price;
    const quotedPrice = quotedServicePrices[service.id];
    return typeof quotedPrice === "number" && Number.isFinite(quotedPrice)
      ? quotedPrice
      : null;
  };

  // Monta booking payload legível para o modal
  const buildBookingForModal = (basePayload: any) => {
    const services = serviceSelection.multiEnabled
      ? serviceSelection.services.filter((s) =>
          serviceSelection.selectedServiceIds.includes(s.id)
        )
      : serviceSelection.services.filter(
          (s) => s.id === serviceSelection.selectedServiceId
        );

    const serviceNames = services.map((s: any) => s.name);

    let serviceName: string | undefined;
    if (serviceNames.length === 1) {
      serviceName = serviceNames[0];
    } else if (serviceNames.length === 2) {
      serviceName = `${serviceNames[0]} e ${serviceNames[1]}`;
    } else if (serviceNames.length > 2) {
      serviceName = serviceNames.join(", ");
    } else {
      serviceName = undefined;
    }

    return {
      ...basePayload,
      serviceName,
      services,
    };
  };

  const submitBookingFlow = async (
    payload: any,
    authToken: string | null,
    isCurrent: () => boolean,
    context?: BookingDecisionContext
  ): Promise<BookingDecisionSubmitResult> => {
    if (!isCurrent()) return false;
    if (!establishmentId || !slug) {
      booking.setError("Estabelecimento inválido.");
      return false;
    }

    const payloadWithProducts = {
      ...payload,
      products: bookingProducts.length > 0 ? bookingProducts : undefined,
    };

    let enabled = false;
    try {
      enabled = await loadPaymentStatus(establishmentId, true);
    } catch {
      if (!isCurrent()) return false;
      booking.setError(
        "Não foi possível verificar a forma de pagamento. Tente novamente antes de confirmar."
      );
      return false;
    }

    // O contato/seleção pode mudar enquanto a configuração de pagamento é
    // consultada. Nenhum POST ou hold pode usar a tentativa invalidada.
    if (!isCurrent()) return false;

    if (!enabled) {
      try {
        const created = await booking.submitBooking(
          payload,
          availability.providerSlots,
          providerSelection.providers,
          availability.slots,
          authToken,
          isCurrent
        );
        if (!isCurrent() || !created) return false;
        availability.refetch();
        setSelectedTime("");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return true;
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error("Erro no fluxo offline:", err);
      }
      return false;
    }

    try {
      if (
        context?.resume?.hold.expiresAt &&
        new Date(context.resume.hold.expiresAt).getTime() <= Date.now()
      )
        throw new Error("A reserva expirou. Selecione o horário novamente.");
      const selectedServiceIds = serviceSelection.multiEnabled
        ? serviceSelection.selectedServiceIds
        : [serviceSelection.selectedServiceId];
      const coverage = authToken ? context?.coverage : undefined;
      const coverageAmount = coverage?.amountDueCents;
      const hasCoverageAmount =
        typeof coverageAmount === "number" &&
        Number.isFinite(coverageAmount) &&
        coverageAmount >= 0;
      const fullyCovered =
        coverage?.authenticated &&
        coverage.covered &&
        !coverage.partial &&
        !coverage.serviceCoverages?.some((item) => !item.coveredByMembership) &&
        (!hasCoverageAmount || coverageAmount === 0);
      const serviceDue = hasCoverageAmount
        ? coverageAmount
        : fullyCovered
          ? 0
          : undefined;
      const confirmedBillableIds = coverage?.serviceCoverages
        ?.filter((item) => !item.coveredByMembership)
        .map((item) => item.serviceId);
      const quotedServiceIds = confirmedBillableIds?.length
        ? confirmedBillableIds
        : selectedServiceIds;
      let amount = context?.resume?.quotedAmountCents ?? 0;
      if (!context?.resume && !fullyCovered) {
        const quote = await getPriceQuote({
          establishmentId,
          paymentType: "credit",
          creditPayoutTerm: "D14",
          ...(serviceDue !== undefined
            ? { amountCents: serviceDue }
            : { serviceIds: quotedServiceIds }),
        }).catch(() => {
          throw new Error(
            "Não foi possível confirmar o valor final do pagamento. Tente novamente."
          );
        });
        if (!isCurrent()) return false;
        amount = Math.round(Number(quote.finalAmount) * 100);
        if (!Number.isFinite(amount) || amount <= 0)
          throw new Error("Não foi possível calcular o valor final. Tente novamente.");
      }
      if (!isCurrent()) return false;
      const hold =
        context?.resume?.hold ??
        (await createPublicAppointmentHold(
          slug,
          payloadWithProducts,
          authToken || undefined
        ));

      if (!isCurrent()) return false;

      // MBR-11: com o valor devido do servidor, só ele decide se há o que
      // pagar. Cobertura parcial sobra valor e segue para o checkout. Resposta
      // antiga (sem `amountDueCents`) mantém a regra anterior.
      const hasServerAmountDue = hold?.amountDueCents !== undefined;
      if (
        hasServerAmountDue &&
        (!Number.isFinite(hold.amountDueCents) || Number(hold.amountDueCents) < 0)
      ) {
        throw new Error("Não foi possível confirmar o valor devido. Tente novamente.");
      }
      const coverageChanged =
        coverage?.serviceCoverages &&
        hold.serviceCoverages &&
        JSON.stringify(
          coverage.serviceCoverages
            .map((item) => [item.serviceId, item.coveredByMembership])
            .sort()
        ) !==
          JSON.stringify(
            hold.serviceCoverages
              .map((item) => [item.serviceId, item.coveredByMembership])
              .sort()
          );
      if (
        !context?.resume &&
        hasServerAmountDue &&
        ((serviceDue !== undefined && Number(hold.amountDueCents) !== serviceDue) ||
          coverageChanged)
      ) {
        const due = Number(hold.amountDueCents);
        return {
          resume: { hold, quotedAmountCents: amount },
          confirmation: {
            name: payload.name,
            message: "O valor ou a cobertura mudou. Confira antes de continuar.",
            amountLabel: `Total de serviços a pagar: ${(due / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`,
            confirmLabel:
              due > 0
                ? `Continuar e pagar ${(due / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
                : "Confirmar agendamento",
            services: (hold.serviceCoverages || []).map((item) => ({
              id: item.serviceId,
              name:
                serviceSelection.services.find((service) => service.id === item.serviceId)
                  ?.name || "Serviço",
              label: item.coveredByMembership
                ? "Coberto pela assinatura"
                : "Fora da assinatura",
            })),
          },
        };
      }
      const legacyHasBillableServices = Boolean(
        (serviceDue !== undefined && serviceDue > 0) ||
        coverage?.partial ||
        hold.membershipRequiresPayment ||
        hold.serviceCoverages?.some((item) => !item.coveredByMembership)
      );
      const skipPayment = hasServerAmountDue
        ? hold.amountDueCents === 0
        : !legacyHasBillableServices &&
          Boolean(hold?.coveredByMembership || hold?.status === "confirmed");

      if (skipPayment) {
        await booking.submitBookingAfterPayment(
          {
            ...payload,
            appointmentId: hold.id,
            expiresAt: hold.expiresAt,
          },
          {
            status: hold.coveredByMembership ? "membership_covered" : "confirmed",
            coveredByMembership: Boolean(hold.coveredByMembership),
            appointmentId: hold.id,
          }
        );
        if (!isCurrent()) return false;
        availability.refetch();
        setSelectedTime("");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return true;
      }

      const serviceCoverages = Array.isArray(hold.serviceCoverages)
        ? hold.serviceCoverages
        : [];
      const coveredServiceIds = serviceCoverages
        .filter((item) => item.coveredByMembership)
        .map((item) => item.serviceId);
      const billableServiceIds = serviceCoverages
        .filter((item) => !item.coveredByMembership)
        .map((item) => item.serviceId);

      const modalBooking = buildBookingForModal({
        ...payload,
        appointmentId: hold.id,
        expiresAt: hold.expiresAt,
      });
      // O resumo do checkout marca "Coberto pela assinatura" serviço a serviço.
      const bookingForPayment =
        coveredServiceIds.length > 0
          ? {
              ...modalBooking,
              services: modalBooking.services.map((service: any) => ({
                ...service,
                coveredByMembership: coveredServiceIds.includes(service.id),
              })),
            }
          : modalBooking;

      if (!isCurrent()) return false;
      trackPublicFunnelEvent(slug, {
        eventName: "checkout_started",
        serviceId: serviceSelection.multiEnabled
          ? serviceSelection.selectedServiceIds[0]
          : serviceSelection.selectedServiceId || undefined,
        providerId: providerSelection.selectedProviderId || undefined,
        scheduledDate: payload.date,
        scheduledTime: payload.time,
        metadata: {
          amountCents: hasServerAmountDue ? Number(hold.amountDueCents) : amount,
          multi: serviceSelection.multiEnabled,
          serviceIds: serviceSelection.multiEnabled
            ? serviceSelection.selectedServiceIds
            : undefined,
        },
      });
      const pendingPayment = {
        slug,
        establishmentId,
        establishmentName: establishment?.name || undefined,
        // O servidor calcula o devido (só os serviços fora da assinatura).
        amountCents: hasServerAmountDue ? Number(hold.amountDueCents) : amount,
        ...(serviceCoverages.length > 0 ? { billableServiceIds, coveredServiceIds } : {}),
        payerEmail: payload.email,
        passCardFeesToClient: establishment?.passCardFeesToClient ?? null,
        pendingSubmitPayload: payload,
        bookingPayload: bookingForPayment,
        hold: { appointmentId: hold.id, expiresAt: hold.expiresAt ?? null },
        createdAt: new Date().toISOString(),
      };

      if (!isCurrent()) return false;
      if (typeof window !== "undefined") {
        sessionStorage.setItem(PAYMENT_SESSION_KEY, JSON.stringify(pendingPayment));
      }

      if (!isCurrent()) return false;
      router.push(`/checkout/pagamento?slug=${encodeURIComponent(slug)}`);
      return true;
    } catch (err: any) {
      if (!isCurrent()) return false;
      booking.setError(err?.message || "Erro ao iniciar pagamento.");
      return false;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (decision.busy || decision.submitting || decision.open || isLoadingCustomer)
      return;
    await submitBookingRequest();
  };

  const submitBookingRequest = async () => {
    const hasService = serviceSelection.multiEnabled
      ? serviceSelection.selectedServiceIds.length > 0
      : Boolean(serviceSelection.selectedServiceId);
    if (!hasService) {
      booking.setError("Selecione um serviço");
      return;
    }

    if (!allowAnyProvider && !providerSelection.selectedProviderId) {
      booking.setError("Escolha um profissional para continuar.");
      return;
    }

    if (!date || !selectedTime) {
      booking.setError("Selecione data e horário");
      return;
    }

    const contactValidationError = getBookingContactValidationError(formData);
    if (contactValidationError) {
      booking.setError(contactValidationError);
      return;
    }

    const validationError = customFields.validateCustomFields();
    if (validationError) {
      booking.setError(validationError);
      return;
    }

    const payload: any = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      whatsapp: formData.whatsapp || undefined,
      phone: formData.phone || undefined,
      notes: formData.notes || undefined,
      date,
      time: selectedTime,
      serviceId: !serviceSelection.multiEnabled
        ? serviceSelection.selectedServiceId || undefined
        : undefined,
      serviceIds: serviceSelection.multiEnabled
        ? serviceSelection.selectedServiceIds
        : undefined,
      providerId: providerSelection.selectedProviderId || undefined,
      customFields: Object.keys(customFields.customValues).length
        ? customFields.customValues
        : undefined,
    };

    await decision.begin(payload);
  };

  const handleCloseMembershipAlertModal = () => {
    setShowMembershipAlertModal(false);
  };

  const openPublicProductsModal = (product?: PublicProduct) => {
    const fallbackProduct =
      product ||
      selectedPublicOrderProduct ||
      publicProducts.find((item) => item.available) ||
      publicProducts[0];
    if (!fallbackProduct) return;

    setPublicOrderMessage(null);
    setPublicOrderForm((previous) => ({
      ...previous,
      customerName: previous.customerName || customerProfile?.fullName || formData.name,
      customerWhatsapp:
        previous.customerWhatsapp || customerProfile?.whatsapp || formData.whatsapp,
      customerEmail: previous.customerEmail || customerProfile?.email || formData.email,
      selectedProductId: fallbackProduct.id,
      quantity:
        maskQuantityInput(
          previous.quantity || "1",
          unitAllowsFraction(fallbackProduct.unitLabel)
        ) || "1",
    }));
    setShowPublicProductsModal(true);
  };

  const handlePublicProductOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!slug || !publicOrderForm.selectedProductId) return;

    setPublicOrderLoading(true);
    setPublicOrderMessage(null);
    try {
      const selectedProduct = publicProducts.find(
        (item) => item.id === publicOrderForm.selectedProductId
      );
      if (!selectedProduct) {
        throw new Error("Selecione um produto para encomendar.");
      }
      if (!selectedProduct.available) {
        throw new Error("Este produto está indisponível para encomenda.");
      }

      const quantity = quantityInputToNumber(publicOrderForm.quantity);
      if (quantity <= 0) {
        throw new Error("Informe uma quantidade válida.");
      }
      if (quantity > Number(selectedProduct.currentStock || 0)) {
        throw new Error("A quantidade informada é maior que o estoque disponível.");
      }

      setBookingProducts((current) => {
        const existing = current.find((item) => item.productId === selectedProduct.id);
        if (!existing) return [...current, { productId: selectedProduct.id, quantity }];
        return current.map((item) =>
          item.productId === selectedProduct.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      });

      setPublicOrderMessage(
        "Produto adicionado ao seu agendamento. Ele será reservado ao confirmar o horário."
      );
      setPublicOrderForm((previous) => ({
        ...previous,
        quantity: "1",
        notes: "",
      }));
    } catch (requestError: any) {
      setPublicOrderMessage(
        String(requestError?.message || "Não foi possível enviar a encomenda.")
      );
    } finally {
      setPublicOrderLoading(false);
    }
  };

  const normalizedMembershipPlanIndex = Math.min(
    activeMembershipPlanIndex,
    Math.max(publicMembershipPlans.length - 1, 0)
  );
  const highlightedMembershipPlan = hasPublicMembershipPlans
    ? publicMembershipPlans[normalizedMembershipPlanIndex]
    : null;
  const greetingName =
    String(formData.name || "")
      .trim()
      .split(/\s+/)[0] || "Cliente";
  const customerAreaRedirectWithSlug = slug
    ? `/cliente/area?slug=${encodeURIComponent(slug)}`
    : "/cliente/area";

  /**
   * Assinar exige conta. Quem esta dentro vai direto para a aba de
   * assinaturas; quem nao esta passa pelo login e volta para lá.
   */
  const membershipAreaHref = slug
    ? `/cliente/area?slug=${encodeURIComponent(slug)}&tab=assinaturas`
    : "/cliente/area?tab=assinaturas";
  const membershipHref = isCustomerLoggedIn
    ? membershipAreaHref
    : `/cliente/login?redirect=${encodeURIComponent(membershipAreaHref)}`;
  const highlightedPlanServices =
    highlightedMembershipPlan?.coverageScope === "all_services"
      ? ["Todos os serviços do estabelecimento"]
      : (highlightedMembershipPlan?.serviceRules || []).map((rule) => {
          const serviceName =
            serviceSelection.services.find((service) => service.id === rule.serviceId)
              ?.name || "Serviço";
          if (rule.monthlyLimit == null) return `${serviceName}: ilimitado`;
          return `${serviceName}: ${rule.monthlyLimit} uso(s)/mês`;
        });
  const compactHighlightedPlanServices =
    highlightedPlanServices.length > 3
      ? [
          ...highlightedPlanServices.slice(0, 2),
          `+${highlightedPlanServices.length - 2} benefícios no plano`,
        ]
      : highlightedPlanServices;
  const displayedHighlightedPlanServices =
    compactHighlightedPlanServices.length > 0
      ? compactHighlightedPlanServices
      : ["Cobertura personalizada para serviços selecionados"];
  const hasMultipleMembershipPlans = publicMembershipPlans.length > 1;
  const highlightedPlanPrice = highlightedMembershipPlan
    ? (Number(highlightedMembershipPlan.monthlyPriceCents || 0) / 100).toLocaleString(
        "pt-BR",
        {
          style: "currency",
          currency: "BRL",
        }
      )
    : null;
  const highlightedPlanInitial = String(highlightedMembershipPlan?.name || "Plano")
    .trim()
    .charAt(0)
    .toUpperCase();
  const modalAccentColor = normalizeHexColor(establishment?.backgroundColor) || "#2563EB";
  const modalAccentTextOnSolid = getReadableTextColor(modalAccentColor);
  const modalBorderColor = hexToRgba(modalAccentColor, 0.16) || "rgba(37, 99, 235, 0.16)";
  const modalSoftSurface = hexToRgba(modalAccentColor, 0.05) || "#F8FAFF";
  const modalSoftSurfaceStrong = hexToRgba(modalAccentColor, 0.1) || "#EEF4FF";
  const modalSoftBorder = hexToRgba(modalAccentColor, 0.18) || "rgba(37, 99, 235, 0.18)";
  const modalGlow = hexToRgba(modalAccentColor, 0.14) || "rgba(37, 99, 235, 0.14)";
  const modalNeutralPanel = "#F8FAFC";
  const modalNeutralBorder = "rgba(15, 23, 42, 0.08)";
  const modalStrongText = "rgba(15, 23, 42, 0.96)";
  const modalMediumText = "rgba(15, 23, 42, 0.64)";
  const hasSelectedService = hasSelectedServiceForWizard;
  const selectedServicesForSummary = serviceSelection.multiEnabled
    ? serviceSelection.services.filter((service) =>
        serviceSelection.selectedServiceIds.includes(service.id)
      )
    : serviceSelection.services.filter(
        (service) => service.id === serviceSelection.selectedServiceId
      );
  const selectedServiceSummary =
    selectedServicesForSummary.length === 0
      ? "Escolha um serviço"
      : selectedServicesForSummary.length === 1
        ? selectedServicesForSummary[0].name
        : `${selectedServicesForSummary.length} serviços selecionados`;
  const selectedProviderSummary =
    providerSelection.selectedProvider?.name ||
    (allowAnyProvider ? "Qualquer profissional" : "Escolha um profissional");
  const selectedDurationMinutes = selectedServicesForSummary.reduce(
    (total, service) => total + Number(service.durationMinutes || 0),
    0
  );
  const selectedPriceLabel = buildBookingSummaryPriceLabel(
    selectedServicesForSummary,
    getServiceDisplayedPrice
  );
  const summaryPriceLabel = hasSelectedService ? selectedPriceLabel : "A definir";

  const showAuxiliarySections = false;
  const selectedServiceWithImage =
    selectedServicesForSummary.find((service) => service.imageUrl) ||
    selectedServicesForSummary[0] ||
    null;
  const selectedProviderImage = providerSelection.selectedProvider?.imageUrl || null;
  const selectedProviderInitials = String(selectedProviderSummary || "QP")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const persistBookingDraft = () => {
    if (!slug) return;
    saveBookingDraft({
      slug,
      savedAt: new Date().toISOString(),
      stepId: "details",
      selectedServiceId: serviceSelection.selectedServiceId || "",
      selectedServiceIds: serviceSelection.selectedServiceIds || [],
      selectedProviderId: providerSelection.selectedProviderId || "",
      date,
      selectedTime,
      formData,
      customValues: customFields.customValues || {},
    });
  };

  const handleGoToAccountCreation = () => {
    persistBookingDraft();
    router.push(`/cliente/cadastro?redirect=${encodeURIComponent(bookingWizardPath)}`);
  };

  const handleGoToAccountLogin = () => {
    persistBookingDraft();
    router.push(`/cliente/login?redirect=${encodeURIComponent(bookingWizardPath)}`);
  };

  // MBR-16: login por código sem sair da página. O rascunho continua no estado
  // da tela (nada foi desmontado), então só falta a sessão e a próxima etapa.
  const handleCodeLoginSuccess = async (token: string) => {
    await saveCustomerToken(token);
    setCustomerToken(token);
    await loadCustomerSession(token, undefined, () => goToWizardStep("details"));
  };

  const handleContinueWithoutAccount = () => {
    setGuestChoiceConfirmed(true);
    wizard.goNext();
  };

  const handleOpenShowcaseProduct = (product: PublicProduct) => {
    openPublicProductsModal(product);
  };

  // Fecha o modal de sucesso e devolve o cliente ao início do fluxo. Sem isso,
  // fechar o modal revelava o wizard parado na etapa de horário — o horário
  // escolhido já tinha sido consumido pela reserva, então a etapa aparecia
  // vazia ("Nenhum horário disponível"), como se algo tivesse dado errado.
  const handleDismissSuccessModal = () => {
    booking.setShowSuccessModal(false);
    booking.setSuccessMessage(null);
    serviceSelection.setSelectedServiceId("");
    serviceSelection.setSelectedServiceIds([]);
    providerSelection.setSelectedProviderId("");
    setDate(todayYMD());
    setSelectedTime("");
    setAutoJumped(false);
    setQuotedServicePrices({});
    setGuestChoiceConfirmed(false);
    if (slug) clearBookingDraft(slug);
    goToWizardStep("provider");
  };

  if (loading) return <LoadingScreen />;
  // LINK-04: salão inexistente vence qualquer dado anterior em memória.
  if (establishmentNotFound) return <PublicNotFound />;
  if (establishmentError && !hasPublicAccessError)
    return <div style={{ padding: 24, color: "#b00" }}>Erro: {establishmentError}</div>;
  if (!displayEstablishment) return <PublicNotFound />;

  return (
    <div
      style={brandStyle}
      data-testid="public-brand-scope"
      className={`min-h-screen overflow-x-hidden bg-[#f7f8fc] ${
        isBookingMode ? "pb-28 lg:pb-6" : "pb-24"
      }`}
    >
      {isProfileMode && (
        <EstablishmentBanner
          establishment={displayEstablishment}
          bannerStyle={bannerStyle}
          branches={branches}
          onBranchChange={handleBranchChange}
        />
      )}

      {isBookingMode && (
        <BookingWizardTopBar
          establishmentName={displayEstablishment.name}
          backHref={customerRedirectToThisSlug}
        />
      )}

      <main
        className={`mx-auto max-w-[1280px] overflow-x-hidden px-4 pb-10 ${
          isProfileMode ? "pt-4 sm:pt-5" : "pt-4 sm:pt-6"
        }`}
      >
        {booking.successMessage ? (
          <div className="mb-5 rounded-2xl border border-accent-200 bg-accent-50 p-4 text-accent-800 shadow-sm">
            <div className="flex items-start gap-3">
              <FiCheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden />
              <span className="text-sm font-semibold leading-6">
                {booking.successMessage}
              </span>
            </div>
          </div>
        ) : null}
        <ErrorMessage error={booking.error} />

        {isProfileMode ? (
          <ProfileBookingEntry
            establishment={displayEstablishment}
            href={bookingWizardPath}
            label={hasBookingDraft ? "Continuar agendamento" : "Agendar agora"}
            services={serviceSelection.services}
            providers={providerSelection.providers}
            times={availability.slots}
            products={publicProducts}
            membershipPlans={hasPublicMembershipPlans ? publicMembershipPlans : []}
            membershipHref={membershipHref}
            date={date}
            loadingTimes={availability.loadingSlots}
            isLoggedIn={isCustomerLoggedIn}
            isLoadingCustomer={isLoadingCustomer}
            customerFirstName={customerProfile?.fullName?.split(/\s+/)[0]}
            customerPhotoUrl={customerProfile?.photoUrl}
            customerAreaHref={customerAreaRedirectWithSlug}
            onLogout={handleCustomerLogout}
            loginHref={`/cliente/login?redirect=${encodeURIComponent(
              customerRedirectToThisSlug
            )}`}
            signupHref={`/cliente/cadastro?redirect=${encodeURIComponent(
              hasPublicMembershipPlans
                ? customerAreaRedirectWithSlug
                : customerRedirectToThisSlug
            )}`}
          />
        ) : booking.showSuccessModal && booking.lastBooking ? (
          <BookingConfirmationScreen
            booking={booking.lastBooking}
            establishmentName={displayEstablishment.name}
            successMessage={booking.successMessage}
            isCustomerLoggedIn={isCustomerLoggedIn}
            customerAreaHref={customerAreaRedirectWithSlug}
            accountSignupHref={`/cliente/cadastro?redirect=${encodeURIComponent(
              customerRedirectToThisSlug
            )}`}
            onBookAnother={handleDismissSuccessModal}
          />
        ) : (
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
              <BookingWizardHeader
                steps={wizard.steps}
                currentIndex={wizard.currentIndex}
                maxReachableIndex={wizard.maxReachableIndex}
                progress={wizard.progress}
                onStepSelect={wizard.goToStep}
                isStepComplete={wizard.isStepComplete}
              />

              <div
                key={wizard.currentStepId}
                className="animate-fade-in space-y-6 p-4 sm:p-6"
              >
                {wizard.currentStepId === "provider" && (
                  <ProviderSelector
                    providers={providerSelection.providers}
                    selectedProviderId={providerSelection.selectedProviderId}
                    onProviderChange={handleProviderChange}
                    date={date}
                    slots={availability.slots}
                    providerSlots={availability.providerSlots}
                    loadingSlots={availability.loadingSlots}
                    loadingProviderSlots={availability.loadingProviderSlots}
                    allowAnyProvider={allowAnyProvider}
                    embedded
                  />
                )}

                {wizard.currentStepId === "service" && (
                  <>
                    <ServiceSelector
                      establishmentId={establishmentId}
                      services={serviceSelection.services}
                      multiEnabled={serviceSelection.multiEnabled}
                      selectedServiceIds={serviceSelection.selectedServiceIds}
                      selectedServiceId={serviceSelection.selectedServiceId}
                      selectedProviderId={providerSelection.selectedProviderId}
                      selectedProviderName={providerSelection.selectedProvider?.name}
                      onClearProviderFilter={
                        allowAnyProvider
                          ? () => providerSelection.setSelectedProviderId("")
                          : undefined
                      }
                      isSelected={serviceSelection.isSelected}
                      toggleService={(id) => {
                        serviceSelection.toggleService(id);
                        setSelectedTime("");
                      }}
                      onPriceMapChange={setQuotedServicePrices}
                      onlinePaymentsEnabled={onlinePaymentsEnabled}
                      embedded
                    />

                    {availability.publicUnitClosures.length > 0 && (
                      <section
                        className="rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-4 text-amber-950 shadow-sm shadow-amber-100/70"
                        aria-label="Aviso de funcionamento"
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-xl bg-white p-2 text-amber-700 shadow-sm ring-1 ring-amber-100">
                            <FiAlertTriangle aria-hidden="true" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-black text-slate-800">
                              Aviso de funcionamento
                            </p>
                            <div className="mt-3 space-y-2.5">
                              {availability.publicUnitClosures
                                .slice(0, 3)
                                .map((closure) => (
                                  <div
                                    key={closure.date}
                                    className="rounded-xl border border-amber-200/80 bg-white/80 px-3 py-3"
                                  >
                                    <p className="text-sm font-black text-amber-800">
                                      {formatClosureNoticeTitle(closure.date)}
                                    </p>
                                    <p className="mt-1 break-words text-sm font-semibold leading-6 text-slate-700">
                                      <span className="font-black text-slate-900">
                                        Motivo:
                                      </span>{" "}
                                      {closure.publicMessage}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                )}

                {wizard.currentStepId === "schedule" && (
                  <>
                    <DatePicker
                      date={date}
                      availableDates={availability.availableDates}
                      onDateChange={handleDateChange}
                      onNextDate={handleNextDate}
                      embedded
                    />

                    {availability.publicUnitClosure && (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-950">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-xl bg-white p-2 text-amber-700 shadow-sm">
                            <FiAlertTriangle aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-black">
                              Unidade sem expediente nesta data
                            </p>
                            <p className="mt-1 break-words text-sm font-semibold leading-6 text-amber-900">
                              {availability.publicUnitClosure.publicMessage}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <ConflictSuggestions
                      suggested={booking.suggested}
                      providers={providerSelection.providers}
                      showSuggestions={booking.showSuggestions}
                      onSelectSuggestion={handleSuggestionSelect}
                      onHideSuggestions={() => booking.setShowSuggestions(false)}
                    />

                    <TimeSlotGrid
                      slots={availability.slots}
                      fitSlots={availability.fitSlots}
                      selectedTime={selectedTime}
                      onTimeSelect={handleTimeSelect}
                      loadingSlots={availability.loadingSlots}
                      providerSlots={availability.providerSlots}
                      selectedProviderId={providerSelection.selectedProviderId}
                      availabilityMeta={availability.availabilityMeta}
                      requiresProviderSelection={!allowAnyProvider}
                      suggestedDate={availability.suggestedDate}
                      loadingSuggestedDate={availability.loadingSuggestedDate}
                      onSelectSuggestedDate={handleDateChange}
                      embedded
                    />
                  </>
                )}

                {wizard.currentStepId === "showcase" && (
                  <BookingShowcaseStep
                    products={publicProducts}
                    onSelectProduct={handleOpenShowcaseProduct}
                    onOpenAll={() => openPublicProductsModal()}
                  />
                )}

                {wizard.currentStepId === "account" && (
                  <BookingAccountStep
                    hasMembershipPlans={hasPublicMembershipPlans}
                    guestChoiceConfirmed={guestChoiceConfirmed}
                    onCreateAccount={handleGoToAccountCreation}
                    onLogin={handleGoToAccountLogin}
                    onContinueAsGuest={handleContinueWithoutAccount}
                    onCodeLogin={handleCodeLoginSuccess}
                    initialEmail={formData.email}
                  />
                )}

                {wizard.currentStepId === "details" && (
                  <>
                    <button
                      type="button"
                      onClick={wizard.goBack}
                      className="inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold leading-none text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
                    >
                      <FiArrowLeft className="h-4 w-4" aria-hidden="true" />
                      Voltar para a etapa anterior
                    </button>

                    <ConflictSuggestions
                      suggested={booking.suggested}
                      providers={providerSelection.providers}
                      showSuggestions={booking.showSuggestions}
                      onSelectSuggestion={handleSuggestionSelect}
                      onHideSuggestions={() => booking.setShowSuggestions(false)}
                    />

                    <BookingForm
                      formData={formData}
                      onFormDataChange={(data) =>
                        setFormData((prev) => ({ ...prev, ...data }))
                      }
                      customFields={customFields.customFields}
                      customValues={customFields.customValues}
                      onCustomValuesChange={customFields.setCustomValues}
                      selectedTime={selectedTime}
                      date={date}
                      selectedProviderId={providerSelection.selectedProviderId}
                      providers={providerSelection.providers}
                      lockedFields={customerLockedFields}
                      isLoggedCustomer={isCustomerLoggedIn}
                      hasSelectedService={hasSelectedService}
                      loading={
                        booking.loading ||
                        decision.busy ||
                        decision.submitting ||
                        isLoadingCustomer
                      }
                      compact
                      onSubmit={handleSubmit}
                    />
                  </>
                )}
              </div>

              {!wizard.isLastStep && (
                <BookingWizardNav
                  canAdvance={wizard.canAdvance}
                  blockedReason={wizard.blockedReason}
                  isFirstStep={wizard.isFirstStep}
                  optionalStep={wizard.currentStep.optional}
                  summaryLabel={
                    hasSelectedService
                      ? `${selectedServiceSummary} · ${selectedPriceLabel}`
                      : null
                  }
                  onBack={wizard.goBack}
                  onNext={wizard.goNext}
                />
              )}
            </div>

            <aside className="lg:sticky lg:top-5 lg:self-start">
              <div className="mb-4 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
                <p className="text-[26px] font-semibold leading-tight text-[#1f2e52]">
                  Resumo do agendamento
                </p>
                <div className="mt-4 rounded-2xl border border-slate-200 p-3">
                  <div className="flex items-center gap-3">
                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-slate-200">
                      {selectedServiceWithImage?.imageUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={resolveImageUrl(selectedServiceWithImage.imageUrl)}
                          alt={selectedServiceWithImage.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm font-black text-slate-500">
                          {hasSelectedService ? "SV" : "--"}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-base font-semibold leading-tight text-[#223259]">
                        {selectedServiceSummary}
                      </p>
                      <div className="mt-1 inline-flex items-center gap-2 text-sm text-slate-600">
                        <FiClock className="h-4 w-4" aria-hidden />
                        {selectedDurationMinutes > 0
                          ? formatDurationMinutes(selectedDurationMinutes)
                          : "Duração a definir"}
                      </div>
                    </div>
                    <p className="max-w-[110px] text-right text-sm font-semibold text-primary-700">
                      {summaryPriceLabel}
                    </p>
                  </div>

                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 overflow-hidden rounded-full bg-primary-100 text-primary-700">
                        {selectedProviderImage ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={resolveImageUrl(selectedProviderImage)}
                            alt={selectedProviderSummary}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[11px] font-black">
                            {selectedProviderInitials || "QP"}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-[#24335a]">
                          {selectedProviderSummary}
                        </p>
                        <p className="text-sm text-slate-500">
                          {getBookingSummaryProviderSubtitle(
                            Boolean(providerSelection.selectedProvider)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3 text-sm text-[#24335a]">
                    <div className="inline-flex items-center gap-2">
                      <FiCalendar className="h-4 w-4 text-slate-500" aria-hidden />
                      <span>{formatPublicBookingDate(date)}</span>
                    </div>
                    <div className="inline-flex items-center justify-end gap-2">
                      <FiClock className="h-4 w-4 text-slate-500" aria-hidden />
                      <span>{selectedTime || "Pendente"}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-lg">
                  <span className="text-slate-500">Duração total</span>
                  <span className="font-medium text-slate-700">
                    {selectedDurationMinutes > 0
                      ? formatDurationMinutes(selectedDurationMinutes)
                      : "A definir"}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between border-slate-200 pt-3 text-[26px] font-semibold text-[#1f2e52]">
                  <span>Total</span>
                  <span className="text-primary-700">{summaryPriceLabel}</span>
                </div>
              </div>
            </aside>
          </section>
        )}
      </main>

      {showAuxiliarySections && (
        <ProviderTimeSlotsGrid
          providers={providerSelection.providers}
          providerSlots={availability.providerSlots}
          selectedProviderId={providerSelection.selectedProviderId}
          selectedTime={selectedTime}
          loadingProviderSlots={availability.loadingProviderSlots}
          onSlotClick={handleProviderSlotClick}
        />
      )}

      {showPublicProductsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/45 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Vitrine do estabelecimento
                  </p>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Produtos para encomenda
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPublicProductsModal(false)}
                  className="inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold leading-none text-slate-600 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
                >
                  Fechar
                </button>
              </div>

              <div className="grid max-h-[calc(100dvh-9rem)] overflow-y-auto lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="border-b border-slate-100 p-4 lg:border-b-0 lg:border-r">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {publicProducts.map((product) => {
                      const isSelected = product.id === publicOrderForm.selectedProductId;
                      return (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => {
                            setPublicOrderMessage(null);
                            setPublicOrderForm((previous) => ({
                              ...previous,
                              selectedProductId: product.id,
                              quantity:
                                maskQuantityInput(
                                  previous.quantity || "1",
                                  unitAllowsFraction(product.unitLabel)
                                ) || "1",
                            }));
                          }}
                          className={`flex gap-3 rounded-xl border p-3 text-left transition ${
                            isSelected
                              ? "border-primary-400 bg-primary-50 shadow-sm shadow-primary-100/70"
                              : "border-slate-200 hover:border-primary-300 hover:bg-primary-50/50"
                          }`}
                        >
                          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                            {product.imageUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-xs text-slate-400">
                                Sem foto
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="line-clamp-2 text-sm font-semibold text-slate-900">
                              {product.name}
                            </h4>
                            <p className="mt-1 text-sm font-semibold text-slate-800">
                              {formatPublicProductPrice(product.salePriceCents)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {formatQuantityForUnit(
                                Number(product.currentStock || 0),
                                product.unitLabel
                              )}{" "}
                              em estoque
                            </p>
                            <span
                              className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                product.available
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {product.available ? "Disponível" : "Indisponível"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <form onSubmit={handlePublicProductOrder} className="space-y-3 p-4">
                  {selectedPublicOrderProduct && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <h4 className="font-semibold text-slate-900">
                        {selectedPublicOrderProduct.name}
                      </h4>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {formatPublicProductPrice(
                          selectedPublicOrderProduct.salePriceCents
                        )}
                      </p>
                      {selectedPublicOrderProduct.description && (
                        <p className="mt-2 text-sm text-slate-600">
                          {selectedPublicOrderProduct.description}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-slate-500">
                        Estoque:{" "}
                        {formatQuantityForUnit(
                          Number(selectedPublicOrderProduct.currentStock || 0),
                          selectedPublicOrderProduct.unitLabel
                        )}
                      </p>
                    </div>
                  )}

                  {customerProfile && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                      Usando dados da sua conta. Você pode ajustar o contato desta
                      encomenda antes de enviar.
                    </div>
                  )}

                  <select
                    aria-label="Produto"
                    className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-800 transition hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    value={publicOrderForm.selectedProductId}
                    onChange={(event) => {
                      const product = publicProducts.find(
                        (item) => item.id === event.target.value
                      );
                      setPublicOrderMessage(null);
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        selectedProductId: event.target.value,
                        quantity:
                          maskQuantityInput(
                            previous.quantity || "1",
                            unitAllowsFraction(product?.unitLabel || "un")
                          ) || "1",
                      }));
                    }}
                    required
                  >
                    <option value="">Selecione o produto</option>
                    {publicProducts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <input
                    aria-label="Seu nome"
                    className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-800 transition placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    placeholder="Seu nome"
                    value={publicOrderForm.customerName}
                    onChange={(event) =>
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        customerName: event.target.value,
                      }))
                    }
                    required
                  />
                  <input
                    aria-label="WhatsApp"
                    className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-800 transition placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    placeholder="WhatsApp"
                    value={publicOrderForm.customerWhatsapp}
                    onChange={(event) =>
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        customerWhatsapp: formatWhatsapp(event.target.value),
                      }))
                    }
                  />
                  <input
                    aria-label="E-mail"
                    className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-800 transition placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    placeholder="E-mail"
                    type="email"
                    value={publicOrderForm.customerEmail}
                    onChange={(event) =>
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        customerEmail: event.target.value,
                      }))
                    }
                  />
                  <input
                    aria-label="Quantidade"
                    className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-800 transition placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    inputMode={
                      selectedPublicProductAllowsFraction ? "decimal" : "numeric"
                    }
                    placeholder={
                      selectedPublicOrderProduct
                        ? `Quantidade em ${selectedPublicOrderProduct.unitLabel}`
                        : "Quantidade"
                    }
                    value={publicOrderForm.quantity}
                    onChange={(event) =>
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        quantity: maskQuantityInput(
                          event.target.value,
                          selectedPublicProductAllowsFraction
                        ),
                      }))
                    }
                    required
                  />
                  <textarea
                    aria-label="Observação opcional"
                    className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 transition placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-100"
                    placeholder="Observação opcional"
                    value={publicOrderForm.notes}
                    onChange={(event) =>
                      setPublicOrderForm((previous) => ({
                        ...previous,
                        notes: event.target.value,
                      }))
                    }
                  />

                  {selectedPublicOrderProduct &&
                    !selectedPublicOrderProduct.available && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        Este produto está indisponível para encomenda no momento.
                      </div>
                    )}

                  {selectedPublicOrderProduct &&
                    selectedPublicOrderProduct.available &&
                    publicOrderQuantity >
                      Number(selectedPublicOrderProduct.currentStock || 0) && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        A quantidade informada é maior que o estoque disponível.
                      </div>
                    )}

                  {publicOrderMessage && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      {publicOrderMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="inline-flex min-h-[3.5rem] w-full items-center justify-center rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-3.5 text-base font-bold leading-none text-white shadow-lg shadow-primary-600/25 transition hover:from-primary-700 hover:to-primary-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:opacity-70"
                    disabled={publicOrderLoading || !publicOrderCanSubmit}
                  >
                    {publicOrderLoading ? "Adicionando..." : "Adicionar ao agendamento"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMembershipAlertModal &&
        !isCustomerLoggedIn &&
        hasPublicMembershipPlans &&
        highlightedMembershipPlan && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="membership-alert-title"
            data-pwa-install-blocker="true"
          >
            <div className="flex min-h-full items-center justify-center">
              <div
                className="relative w-full max-w-[960px] overflow-hidden rounded-[28px] border bg-white shadow-2xl"
                style={{
                  borderColor: modalBorderColor,
                  boxShadow: `0 28px 90px ${modalGlow}`,
                }}
              >
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 h-40"
                  style={{
                    background: `radial-gradient(circle at top left, ${modalSoftSurfaceStrong} 0%, rgba(255,255,255,0) 72%)`,
                  }}
                />

                <button
                  type="button"
                  aria-label="Fechar modal"
                  onClick={handleCloseMembershipAlertModal}
                  className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white/95 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                  style={{ borderColor: modalSoftBorder }}
                >
                  X
                </button>

                <div className="relative p-5 sm:p-7">
                  <div className="flex flex-wrap items-center justify-between gap-3 pr-12">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]"
                        style={{
                          backgroundColor: modalSoftSurfaceStrong,
                          color: modalAccentColor,
                        }}
                      >
                        Assinatura do cliente
                      </span>
                      <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                        Plano {normalizedMembershipPlanIndex + 1} de{" "}
                        {publicMembershipPlans.length}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-6 lg:grid-cols-[264px_minmax(0,1fr)] lg:items-start">
                    <div className="space-y-3">
                      <div
                        className="relative overflow-hidden rounded-[24px] p-2"
                        style={{
                          backgroundColor: "#F3F7FB",
                          border: `1px solid ${modalNeutralBorder}`,
                        }}
                      >
                        <div className="relative">
                          {highlightedMembershipPlan.imageUrl ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={resolveImageUrl(highlightedMembershipPlan.imageUrl)}
                              alt={`Plano ${highlightedMembershipPlan.name}`}
                              className="h-[228px] w-full rounded-[18px] object-cover"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                                const fallback = event.currentTarget
                                  .nextElementSibling as HTMLElement | null;
                                fallback?.classList.remove("hidden");
                                fallback?.classList.add("flex");
                              }}
                            />
                          ) : null}
                          <div
                            className={`${
                              highlightedMembershipPlan.imageUrl ? "hidden" : "flex"
                            } h-[228px] w-full items-center justify-center rounded-[18px]`}
                            style={{
                              backgroundImage: `linear-gradient(180deg, ${modalSoftSurfaceStrong} 0%, ${modalNeutralPanel} 100%)`,
                            }}
                          >
                            <div className="text-center">
                              <div
                                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold"
                                style={{
                                  backgroundColor: modalAccentColor,
                                  color: modalAccentTextOnSolid,
                                }}
                              >
                                {highlightedPlanInitial}
                              </div>
                              <p className="mt-3 text-sm font-medium text-slate-500">
                                Plano sem imagem
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="pointer-events-none absolute inset-x-5 bottom-5">
                          <div
                            className="inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-slate-900 shadow-sm backdrop-blur-sm"
                            style={{
                              backgroundColor: "rgba(255,255,255,0.92)",
                              borderColor: modalNeutralBorder,
                            }}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: modalAccentColor }}
                            />
                            <span className="truncate text-sm font-medium">
                              {highlightedMembershipPlan.name}
                            </span>
                          </div>
                        </div>
                      </div>

                      {hasMultipleMembershipPlans && (
                        <div
                          className="flex items-center justify-between rounded-full p-1.5"
                          style={{
                            backgroundColor: modalNeutralPanel,
                            border: `1px solid ${modalNeutralBorder}`,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setActiveMembershipPlanIndex((current) =>
                                current <= 0
                                  ? publicMembershipPlans.length - 1
                                  : current - 1
                              )
                            }
                            className="inline-flex min-h-9 items-center rounded-full px-3 text-sm font-semibold text-slate-700 transition hover:bg-white"
                          >
                            Anterior
                          </button>

                          <div className="flex items-center gap-2">
                            {publicMembershipPlans.map((plan, index) => (
                              <button
                                key={plan.id}
                                type="button"
                                aria-label={`Ir para plano ${index + 1}`}
                                onClick={() => setActiveMembershipPlanIndex(index)}
                                className="h-2 rounded-full transition-all"
                                style={{
                                  width: index === normalizedMembershipPlanIndex ? 24 : 8,
                                  backgroundColor:
                                    index === normalizedMembershipPlanIndex
                                      ? modalAccentColor
                                      : "rgba(148, 163, 184, 0.5)",
                                }}
                              />
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              setActiveMembershipPlanIndex((current) =>
                                current >= publicMembershipPlans.length - 1
                                  ? 0
                                  : current + 1
                              )
                            }
                            className="inline-flex min-h-9 items-center rounded-full px-3 text-sm font-semibold text-slate-700 transition hover:bg-white"
                          >
                            Próximo
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col">
                      <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                          <div className="max-w-xl">
                            <h3
                              id="membership-alert-title"
                              className="text-[clamp(1.85rem,3.6vw,2.95rem)] font-semibold leading-[1.01] tracking-[-0.04em]"
                              style={{ color: modalStrongText }}
                            >
                              {greetingName},{" "}
                              {displayEstablishment?.name || "Este estabelecimento"} tem
                              planos para quem agenda com frequência
                            </h3>
                            <p
                              className="mt-3 max-w-xl text-[15px] leading-7"
                              style={{ color: modalMediumText }}
                            >
                              Faça login para contratar sua assinatura, usar seus
                              benefícios e acompanhar seus agendamentos na área do
                              cliente.
                            </p>
                          </div>

                          {highlightedPlanPrice && (
                            <div
                              className="rounded-[24px] px-5 py-4 md:min-w-[212px]"
                              style={{
                                background:
                                  "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                                border: `1px solid ${modalBorderColor}`,
                                boxShadow: `0 12px 26px ${hexToRgba(modalAccentColor, 0.08) || "rgba(37, 99, 235, 0.08)"}`,
                              }}
                            >
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                A partir de
                              </p>
                              <p
                                className="mt-2 text-[2.45rem] font-semibold leading-none tracking-[-0.05em]"
                                style={{ color: modalAccentColor }}
                              >
                                {highlightedPlanPrice}
                              </p>
                              <p className="mt-1 text-sm text-slate-500">
                                cobrança mensal
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="grid gap-4 md:grid-cols-[minmax(0,1.08fr)_minmax(240px,0.92fr)]">
                          <div
                            className="rounded-[24px] p-5"
                            style={{
                              backgroundColor: modalNeutralPanel,
                              border: `1px solid ${modalNeutralBorder}`,
                            }}
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                              Plano
                            </p>
                            <h4
                              className="mt-2 text-[1.85rem] font-semibold leading-[1.02] tracking-[-0.035em]"
                              style={{ color: modalStrongText }}
                            >
                              {highlightedMembershipPlan.name}
                            </h4>
                            {highlightedMembershipPlan.description && (
                              <p
                                className="mt-3 line-clamp-3 text-[15px] leading-7"
                                style={{ color: modalMediumText }}
                              >
                                {highlightedMembershipPlan.description}
                              </p>
                            )}
                          </div>

                          <div
                            className="rounded-[24px] p-5"
                            style={{
                              backgroundColor: modalSoftSurface,
                              border: `1px solid ${modalBorderColor}`,
                            }}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p
                                className="text-[11px] font-semibold uppercase tracking-[0.24em]"
                                style={{ color: modalAccentColor }}
                              >
                                O que inclui
                              </p>
                              <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-slate-500">
                                {highlightedPlanServices.length} item
                                {highlightedPlanServices.length === 1 ? "" : "s"}
                              </span>
                            </div>

                            <div className="mt-4 space-y-1">
                              {displayedHighlightedPlanServices.map((item, index) => (
                                <div
                                  key={`${highlightedMembershipPlan.id}-${index}`}
                                  className="flex items-start gap-3 px-1 py-2.5"
                                  style={{
                                    borderBottom:
                                      index < displayedHighlightedPlanServices.length - 1
                                        ? `1px solid ${modalBorderColor}`
                                        : "none",
                                  }}
                                >
                                  <span
                                    className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full"
                                    style={{ backgroundColor: modalSoftSurfaceStrong }}
                                  >
                                    <span
                                      className="h-2 w-2 rounded-full"
                                      style={{ backgroundColor: modalAccentColor }}
                                    />
                                  </span>
                                  <span className="text-sm leading-6 text-slate-700">
                                    {item}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div
                          className="grid gap-4 rounded-[24px] p-4 sm:grid-cols-[1fr_auto] sm:items-center"
                          style={{
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                            border: `1px solid ${modalNeutralBorder}`,
                          }}
                        >
                          <div className="max-w-lg">
                            <p
                              className="text-sm font-semibold"
                              style={{ color: modalStrongText }}
                            >
                              Use sua conta para contratar, acompanhar consumo e gerenciar
                              seus agendamentos.
                            </p>
                            <p
                              className="mt-1 text-sm leading-6"
                              style={{ color: modalMediumText }}
                            >
                              O login libera a assinatura e centraliza todo o histórico do
                              cliente em um só lugar.
                            </p>
                          </div>

                          <div className="grid w-full gap-2 sm:w-[260px]">
                            <Link
                              href={`/cliente/login?redirect=${encodeURIComponent(customerAreaRedirectWithSlug)}`}
                              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-2xl px-5 text-base font-semibold transition hover:translate-y-[-1px] hover:opacity-95"
                              style={{
                                background: `linear-gradient(135deg, ${modalAccentColor} 0%, ${modalAccentColor} 100%)`,
                                color: modalAccentTextOnSolid,
                                boxShadow: `0 18px 34px ${modalGlow}`,
                              }}
                            >
                              Entrar e assinar
                            </Link>
                            <button
                              type="button"
                              onClick={handleCloseMembershipAlertModal}
                              className="inline-flex min-h-[40px] w-full items-center justify-center rounded-2xl px-5 text-sm font-semibold transition hover:text-slate-700"
                              style={{ color: modalMediumText }}
                            >
                              Continuar sem entrar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {decision.open && (
        <BookingDecisionDialog
          initialEmail={formData.email}
          priceLabel={buildGuestServicePriceLabel(
            serviceSelection.services.filter((service) =>
              serviceSelection.multiEnabled
                ? serviceSelection.selectedServiceIds.includes(service.id)
                : service.id === serviceSelection.selectedServiceId
            ),
            getServiceDisplayedPrice
          )}
          hasProducts={bookingProducts.length > 0}
          busy={decision.busy}
          submitting={decision.submitting}
          error={decision.error}
          notice={decision.notice}
          summary={decision.summary}
          canChooseGuest={decision.canChooseGuest}
          hasMembershipMatch={decision.hasMembershipMatch}
          onClose={decision.close}
          onLogin={decision.login}
          onConfirm={() => void decision.confirm()}
          onRetry={() => void decision.retry()}
          onAdjust={() => {
            decision.close();
            goToWizardStep("schedule");
          }}
        />
      )}

      {customerReviewModal ? (
        <CustomerReviewPromptModal
          review={customerReviewModal}
          submitting={customerReviewSubmitting}
          error={customerReviewError}
          onSubmit={handleSubmitCustomerReview}
          onClose={handleDismissCustomerReview}
        />
      ) : null}

      <ImageModal
        show={showImageModal}
        imageUrl={serviceSelection.selectedService?.imageUrl}
        alt={serviceSelection.selectedService?.name || "Imagem do serviço"}
        onClose={() => setShowImageModal(false)}
      />
    </div>
  );
}
